import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, CreditCard, Clock, Banknote } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { meiosPagamentoService } from '../../services/meiosPagamentoService'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'

export default function MeiosPagamentoPage() {
  const { organizadorAtivo } = useOrganizador()
  const { toasts, success, error, hideToast } = useToast()
  const [meios, setMeios] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'A_VISTA',
    dias_prazo: '',
    primeira_parcela_tipo: 'A_VISTA',
    primeira_parcela_dias: '',
    grupos_parcelas: [{ quantidade: 1, peso: 1, dias: 30 }]
  })

  const organizadorId = organizadorAtivo?.id

  useEffect(() => {
    if (organizadorId) {
      carregarMeios()
    }
  }, [organizadorId])

  const carregarMeios = async () => {
    try {
      setLoading(true)
      const data = await meiosPagamentoService.listar(organizadorId)
      setMeios(data)
    } catch (err) {
      console.error('Erro ao carregar meios de pagamento:', err)
      error('Erro ao carregar meios de pagamento')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'A_VISTA',
      dias_prazo: '',
      primeira_parcela_tipo: 'A_VISTA',
      primeira_parcela_dias: '',
      grupos_parcelas: [{ quantidade: 1, peso: 1, dias: 30 }]
    })
    setEditando(null)
  }

  const handleNovoMeio = () => {
    resetForm()
    setShowModal(true)
  }

  const handleEditar = (meio) => {
    setEditando(meio)
    setFormData({
      nome: meio.nome,
      tipo: meio.tipo,
      dias_prazo: meio.dias_prazo || '',
      primeira_parcela_tipo: meio.primeira_parcela_tipo || 'A_VISTA',
      primeira_parcela_dias: meio.primeira_parcela_dias || '',
      grupos_parcelas: meio.grupos_parcelas?.length > 0
        ? meio.grupos_parcelas.map(g => ({ quantidade: g.quantidade, peso: g.peso, dias: g.dias ?? meio.intervalo_dias ?? 30 }))
        : [{ quantidade: 1, peso: 1, dias: 30 }]
    })
    setShowModal(true)
  }

  const handleExcluir = async (meio) => {
    if (!confirm(`Deseja excluir o meio de pagamento "${meio.nome}"?`)) return

    try {
      await meiosPagamentoService.excluir(organizadorId, meio.id)
      success('Meio de pagamento excluído')
      carregarMeios()
    } catch (err) {
      console.error('Erro ao excluir:', err)
      error('Erro ao excluir meio de pagamento')
    }
  }

  const handleSalvar = async (e) => {
    e.preventDefault()

    if (!formData.nome.trim()) {
      error('Nome é obrigatório')
      return
    }

    if (formData.tipo === 'PARCELADO' && !formData.grupos_parcelas?.length) {
      error('Adicione pelo menos um grupo de parcelas')
      return
    }

    const dados = {
      nome: formData.nome.trim(),
      tipo: formData.tipo,
      dias_prazo: formData.tipo === 'PRAZO' ? parseInt(formData.dias_prazo) || null : null,
      primeira_parcela_tipo: formData.tipo === 'PARCELADO' ? formData.primeira_parcela_tipo : null,
      primeira_parcela_dias: formData.tipo === 'PARCELADO' && formData.primeira_parcela_tipo === 'DIAS' 
        ? parseInt(formData.primeira_parcela_dias) || null 
        : null,
      // Compat: mantém o intervalo do 1º grupo aqui pra telas antigas que só
      // leem esse campo (a fonte da verdade agora é o "dias" de cada grupo).
      intervalo_dias: formData.tipo === 'PARCELADO' ? (formData.grupos_parcelas[0]?.dias || 30) : null,
      grupos_parcelas: formData.tipo === 'PARCELADO' ? formData.grupos_parcelas : null
    }

    try {
      if (editando) {
        await meiosPagamentoService.atualizar(organizadorId, editando.id, dados)
        success('Meio de pagamento atualizado')
      } else {
        await meiosPagamentoService.criar(organizadorId, dados)
        success('Meio de pagamento criado')
      }
      setShowModal(false)
      resetForm()
      carregarMeios()
    } catch (err) {
      console.error('Erro ao salvar:', err)
      error('Erro ao salvar meio de pagamento')
    }
  }

  const addGrupoParcela = () => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: [...prev.grupos_parcelas, { quantidade: 1, peso: 1, dias: 30 }]
    }))
  }

  const removeGrupoParcela = (index) => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: prev.grupos_parcelas.filter((_, i) => i !== index)
    }))
  }

  const updateGrupoParcela = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      grupos_parcelas: prev.grupos_parcelas.map((g, i) => 
        i === index ? { ...g, [field]: parseInt(value) || 0 } : g
      )
    }))
  }

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'A_VISTA': return <Banknote className="w-5 h-5 text-green-600" />
      case 'PRAZO': return <Clock className="w-5 h-5 text-amber-600" />
      case 'PARCELADO': return <CreditCard className="w-5 h-5 text-blue-600" />
      default: return <CreditCard className="w-5 h-5 text-gray-600" />
    }
  }

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'A_VISTA': return 'À Vista'
      case 'PRAZO': return 'A Prazo'
      case 'PARCELADO': return 'Parcelado'
      default: return tipo
    }
  }

  const getDescricaoMeio = (meio) => {
    if (meio.tipo === 'A_VISTA') return 'Pagamento à vista'
    if (meio.tipo === 'PRAZO') return `Prazo de ${meio.dias_prazo || 0} dias`
    if (meio.tipo === 'PARCELADO') {
      const totalParcelas = meio.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade, 0) || 0
      const primeiraParcela = meio.primeira_parcela_tipo === 'A_VISTA' 
        ? 'à vista' 
        : `em ${meio.primeira_parcela_dias || 0} dias`
      const diasUnicos = [...new Set((meio.grupos_parcelas || []).map(g => g.dias ?? meio.intervalo_dias ?? 30))]
      const intervaloTexto = diasUnicos.length === 1 ? `intervalo ${diasUnicos[0]} dias` : 'intervalos variados'
      return `${totalParcelas}x - 1ª parcela ${primeiraParcela} - ${intervaloTexto}`
    }
    return ''
  }

  return (
    <>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} duration={toast.duration} onClose={() => hideToast(toast.id)} />
      ))}

      <div className="page-content">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meios de Pagamento</h1>
            <p className="text-sm text-gray-500">Gerencie os meios de pagamento disponíveis para faturamento</p>
          </div>
          <button onClick={handleNovoMeio} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Meio de Pagamento
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : meios.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Nenhum meio de pagamento cadastrado</h3>
            <p className="text-gray-500 mb-4">Crie meios de pagamento para usar no faturamento dos leilões</p>
            <button onClick={handleNovoMeio} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Meio
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meios.map(meio => (
              <div key={meio.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    {getTipoIcon(meio.tipo)}
                    <div>
                      <h3 className="font-medium text-gray-900">{meio.nome}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {getTipoLabel(meio.tipo)}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button onClick={() => handleEditar(meio)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Editar">
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button onClick={() => handleExcluir(meio)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Excluir">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-3">{getDescricaoMeio(meio)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">
                {editando ? 'Editar Meio de Pagamento' : 'Novo Meio de Pagamento'}
              </h2>
            </div>

            <form onSubmit={handleSalvar} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="input-rematei"
                  placeholder="Ex: 3x iguais, À vista, 30/60/90"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="input-rematei"
                >
                  <option value="A_VISTA">À Vista</option>
                  <option value="PRAZO">A Prazo</option>
                  <option value="PARCELADO">Parcelado</option>
                </select>
              </div>

              {formData.tipo === 'PRAZO' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dias de Prazo</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.dias_prazo}
                    onChange={(e) => setFormData({ ...formData, dias_prazo: e.target.value })}
                    className="input-rematei"
                    placeholder="Ex: 30"
                  />
                </div>
              )}

              {formData.tipo === 'PARCELADO' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento da 1ª Parcela</label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, primeira_parcela_tipo: 'A_VISTA' })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.primeira_parcela_tipo === 'A_VISTA'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        À Vista
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, primeira_parcela_tipo: 'DIAS' })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.primeira_parcela_tipo === 'DIAS'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        A Prazo
                      </button>
                    </div>
                  </div>

                  {formData.primeira_parcela_tipo === 'DIAS' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Dias para 1ª Parcela</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.primeira_parcela_dias}
                        onChange={(e) => setFormData({ ...formData, primeira_parcela_dias: e.target.value })}
                        className="input-rematei"
                        placeholder="Ex: 5"
                      />
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Grupos de Parcelas</label>
                        <p className="text-xs text-gray-400">Cada grupo já vem com 30 dias de intervalo — edite se precisar</p>
                      </div>
                      <button type="button" onClick={addGrupoParcela} className="text-xs text-blue-600 hover:text-blue-800">
                        + Grupo
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.grupos_parcelas.map((grupo, index) => {
                        const isUltimoGrupo = index === formData.grupos_parcelas.length - 1
                        const temProximaParcela = grupo.quantidade > 1 || !isUltimoGrupo
                        return (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-500 w-6">{index + 1}</span>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500">Qtd. parcelas</label>
                            <input
                              type="number"
                              min="1"
                              value={grupo.quantidade}
                              onChange={(e) => updateGrupoParcela(index, 'quantidade', e.target.value)}
                              className="input-rematei text-sm"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500">Peso</label>
                            <input
                              type="number"
                              min="1"
                              value={grupo.peso}
                              onChange={(e) => updateGrupoParcela(index, 'peso', e.target.value)}
                              className="input-rematei text-sm"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500">Dias p/ próxima</label>
                            <input
                              type="number"
                              min="1"
                              disabled={!temProximaParcela}
                              value={temProximaParcela ? (grupo.dias ?? 30) : ''}
                              placeholder={temProximaParcela ? '30' : '—'}
                              onChange={(e) => updateGrupoParcela(index, 'dias', e.target.value)}
                              className={`input-rematei text-sm ${!temProximaParcela ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : ''}`}
                            />
                          </div>
                          {formData.grupos_parcelas.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeGrupoParcela(index)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        )
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Total: {formData.grupos_parcelas.reduce((acc, g) => acc + (g.quantidade || 0), 0)} parcelas
                    </p>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm() }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editando ? 'Salvar Alterações' : 'Criar Meio de Pagamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
